import { AI_REVIEW_MAX_BYTES, type DocumentDTO } from "@zplanem/responses"
import { formatBytes } from "../../../lib/format-bytes"
import { useDocumentAiReview } from "../../hooks/use-documents"
import { getTranslations } from "../../i18n"
import { useLocale } from "../../locale-context"
import {
	aiReviewBadgeEligibility,
	aiReviewStateOf,
} from "./ai-review-badge-model"

const PILL_BASE =
	"inline-flex shrink-0 items-center gap-1 rounded-sm border px-2 py-0.5 text-[10px] font-medium leading-none transition-colors"
const WARNING_TONE =
	"border-warning/40 bg-warning-muted text-warning-muted-foreground"
const FAILED_TONE = "border-destructive/30 bg-destructive/10 text-destructive"

/**
 * Per-document AI contract-review badge (ZPL-209/210). The pure visibility
 * decision lives in `aiReviewBadgeEligibility` (ai-review-badge-model) — it gates
 * on `canDownload` so the badge follows the same CASL read tier as downloading
 * the document (ZPL-211), since the review contains verbatim document quotes.
 * Branches into:
 *  - hidden → not AI-enabled, or the viewer can't download it;
 *  - ineligible (non-PDF or over the AI size ceiling) → a static warning pill;
 *  - eligible → review state (reviewing / failed / done) from `useDocumentAiReview`,
 *    which `useDocumentReviewRealtime` (mounted on the list) keeps fresh.
 *
 * The parent itself uses no hooks, so the eligibility early-returns don't break
 * the rules of hooks — the query lives in `<ReviewStatePill>` alone.
 */
export function AiReviewBadge({
	propertyId,
	document,
	onOpen,
}: {
	propertyId: string
	document: DocumentDTO
	onOpen: () => void
}) {
	const eligibility = aiReviewBadgeEligibility(document)
	if (eligibility === "hidden") return null
	if (eligibility === "ineligible-size") return <IneligiblePill reason="size" />
	if (eligibility === "ineligible-type") {
		return <IneligiblePill reason="type" />
	}

	return (
		<ReviewStatePill
			propertyId={propertyId}
			documentId={document.id}
			onOpen={onOpen}
		/>
	)
}

function IneligiblePill({ reason }: { reason: "size" | "type" }) {
	const { locale } = useLocale()
	const td = getTranslations(locale).projects.documents
	const label = reason === "size" ? td.aiBadgeTooLarge : td.aiBadgeUnsupported
	const hint =
		reason === "size"
			? td.aiBadgeTooLargeHint.replace(
					"{size}",
					formatBytes(AI_REVIEW_MAX_BYTES),
				)
			: td.aiBadgeUnsupportedHint
	return (
		<span className={`${PILL_BASE} ${WARNING_TONE}`} title={hint}>
			{label}
		</span>
	)
}

function ReviewStatePill({
	propertyId,
	documentId,
	onOpen,
}: {
	propertyId: string
	documentId: string
	onOpen: () => void
}) {
	const { locale } = useLocale()
	const td = getTranslations(locale).projects.documents
	const { data: review } = useDocumentAiReview(propertyId, documentId)
	const state = aiReviewStateOf(review)

	// No review row yet (e.g. a document AI-enabled before the feature, or one
	// whose auto-trigger hasn't landed) — show nothing rather than a dead pill.
	if (state === "none") return null

	if (state === "failed") {
		return (
			<button
				type="button"
				onClick={onOpen}
				title={td.aiBadgeFailedHint}
				className={`${PILL_BASE} ${FAILED_TONE} hover:bg-destructive/20`}
			>
				{td.aiBadgeFailed}
			</button>
		)
	}

	if (state === "processed") {
		return (
			<button
				type="button"
				onClick={onOpen}
				title={td.aiBadgeReviewHint}
				className={`${PILL_BASE} ${WARNING_TONE} hover:bg-warning/15`}
			>
				<SparkleIcon />
				{td.aiBadgeReview}
			</button>
		)
	}

	// Pending: a pulsing dot keeps it lightweight across many rows (no per-row
	// timer), and it isn't clickable until there's something to show.
	return (
		<span
			className={`${PILL_BASE} ${WARNING_TONE}`}
			title={td.aiBadgeReviewingHint}
		>
			<span className="h-1.5 w-1.5 animate-pulse rounded-full bg-warning" />
			{td.aiBadgeReviewing}
		</span>
	)
}

function SparkleIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="currentColor"
			className="h-3 w-3"
			aria-hidden="true"
		>
			<path d="M12 2l1.8 5.2L19 9l-5.2 1.8L12 16l-1.8-5.2L5 9l5.2-1.8L12 2z" />
		</svg>
	)
}
